import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, Form, Button, Input, Typography, message, Checkbox, Divider } from 'antd';
import { db } from '../firebase'; // Make sure this path is correct
import { collection, doc, setDoc, updateDoc } from 'firebase/firestore';
import { allPermissions } from '../config/permissions'; // Import your master permissions list

const { Title, Text } = Typography;

const AddRolePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // The role object passed from the list page (if editing)
  const editingRole = location.state?.role;

  // State
  const [loading, setLoading] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  // This hook runs when the component loads to populate the form if we are editing
  useEffect(() => {
    if (editingRole) {
      // If editing, set the form fields with the existing role's data
      form.setFieldsValue({
        roleName: editingRole.id, // The role name is the document ID
        description: editingRole.description || '',
      });
      // Set the initial state of the permission checkboxes
      setSelectedPermissions(editingRole.permissions || []);
    }
  }, [editingRole, form]);

  // Handle form submission
  const handleSubmit = async (values) => {
    const { roleName, description } = values;

    if (!roleName.trim()) {
      message.error('Role name is required.');
      return;
    }

    setLoading(true);

    const roleData = {
      description: description || '',
      permissions: selectedPermissions, // Include the selected permissions
    };

    try {
      if (editingRole && editingRole.id) {
        // --- EDITING an existing role ---
        // We only update the description and permissions, not the name (ID)
        const roleDocRef = doc(db, 'roles', editingRole.id);
        await updateDoc(roleDocRef, roleData);
        message.success('Role updated successfully');
      } else {
        // --- ADDING a new role ---
        // We use setDoc with the roleName as the ID to avoid random IDs
        const roleDocRef = doc(db, 'roles', roleName.trim());
        await setDoc(roleDocRef, roleData);
        message.success('Role created successfully');
      }
      
      setTimeout(() => navigate('/roles'), 800);

    } catch (error) {
      console.error("Error saving role to Firestore: ", error);
      message.error('Failed to save role: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // --- "Select All" Checkbox Handlers ---
  const onSelectAllChange = (e) => {
    setSelectedPermissions(e.target.checked ? allPermissions : []);
  };

  const isAllSelected = selectedPermissions.length === allPermissions.length;
  const isIndeterminate = selectedPermissions.length > 0 && selectedPermissions.length < allPermissions.length;

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <Title level={2} style={{ marginBottom: '24px' }}>
        {editingRole ? 'Edit Role' : 'Add New Role'}
      </Title>
      
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Card>
          <Form.Item
            name="roleName"
            label={<Text strong>Role Name</Text>}
            rules={[{ required: true, message: 'Please enter the role name' }]}
          >
            <Input
              placeholder="e.g., Manager, Cashier"
              size="large"
              // Disable changing the name when editing, as it's the ID
              disabled={!!editingRole} 
            />
          </Form.Item>

          <Form.Item
            name="description"
            label={<Text strong>Description (Optional)</Text>}
          >
            <Input.TextArea
              placeholder="A short description of the role's responsibilities"
              rows={3}
            />
          </Form.Item>
        </Card>

        {/* --- NEW: Permissions Card --- */}
        <Card title="Assign Permissions" style={{ marginTop: '24px' }}>
          <Checkbox
            indeterminate={isIndeterminate}
            onChange={onSelectAllChange}
            checked={isAllSelected}
          >
            Select All
          </Checkbox>
          <Divider />
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            <Checkbox.Group 
              style={{ width: '100%' }} 
              value={selectedPermissions} 
              onChange={setSelectedPermissions}
            >
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {allPermissions.map(permission => (
                  <Checkbox key={permission} value={permission} style={{ margin: '5px 0' }}>
                    {permission}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </div>
        </Card>

        <Form.Item style={{ marginTop: '24px' }}>
          <Button 
            type="primary" 
            htmlType="submit"
            size="large"
            loading={loading}
          >
            {editingRole ? 'Update Role' : 'Save Role'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default AddRolePage;